/** Normalizers et filtres partagés — suivi de production. */

export const normalizeBatch = (b) => ({
  ...b,
  batchNumber: b.batch_number,
  patientName: b.patient_name,
  clinicName: b.clinic_name,
  startDate: b.start_date,
  deadline: b.deadline,
  group: b.current_phase || '1',
  deliveryDeadline: b.deadline,
  dentistName: b.case?.dentist?.full_name || '—',
  assignedMachine: b.machine?.machine_name || null,
  machine: b.machine || null,
  currentPhase: b.status,
  patientCount: 1,
  caseId: b.case_id || null,
  caseNumber: b.case?.case_number || null,
  responsibleId: b.responsible || null,
  responsibleName: b.responsible_user?.full_name || null,
  caseTimelinePhases: (b.case?.case_timeline_phases || []).map((p) => ({
    id: p.id,
    name: p.phase_name,
    phase_name: p.phase_name,
    status: p.status,
    responsible_user_id: p.responsible_user_id,
    phase_order: p.phase_order,
    group: p.group,
  })),
  model_up_from: b.model_up_from ?? 0,
  model_up_to: b.model_up_to ?? 12,
  model_low_from: b.model_low_from ?? 0,
  model_low_to: b.model_low_to ?? 12,
  elastic: b.elastic || '',
  ali_confirmation: b.ali_confirmation || '',
  suppression: b.suppression || '',
  label_from: b.label_from ?? 0,
  label_to: b.label_to ?? 12,
  of_number: b.of_number ?? null,
  packaging_date: b.packaging_date || null,
  createdAt: b.created_at,
  updatedAt: b.updated_at,
});

export const normalizeMachine = (m) => ({
  ...m,
  id: m.id,
  name: m.machine_name,
  model: m.model,
  status: m.status,
  currentJob: m.current_batch?.batch_number || null,
  progress: m.current_batch?.progress || 0,
  estimatedCompletion: m.next_maintenance_date
    ? `Maint. prévue le ${new Date(m.next_maintenance_date).toLocaleDateString('fr-FR')}`
    : m.status === 'available'
      ? 'Disponible'
      : '—',
  utilizationRate: m.current_utilization ?? 0,
});

export const normalizeOperator = (o) => ({
  id: o.id,
  name: o.full_name,
  role: o.department,
  avatar: o.photo_url || null,
  avatarAlt: o.full_name,
  assignedBatches: [],
  workload: Math.round((o.efficiency ?? 0) * 100),
  shift: o.shift || '—',
  status: o.is_active ? 'active' : 'inactive',
});

/** Lot actif avec échéance ≤ 2 jours (aligné filtres / KPI). */
export function isCriticalDeadlineBatch(batch, now = Date.now()) {
  if (!batch?.deadline || batch.status === 'completed') return false;
  const urgentDate = new Date(now + 2 * 24 * 60 * 60 * 1000);
  const d = new Date(batch.deadline);
  if (Number.isNaN(d.getTime())) return false;
  return d <= urgentDate;
}

export function countCriticalDeadlineLots(batches, now = Date.now()) {
  return (batches || []).filter((b) => isCriticalDeadlineBatch(b, now)).length;
}

/** @deprecated Préférer le chip filtre « Délais critiques » — conservé pour compat. */
export function buildProductionAlerts(batches) {
  return (batches || [])
    .filter((b) => isCriticalDeadlineBatch(b))
    .slice(0, 5)
    .map((b) => ({
      id: `urgent-${b.id}`,
      type: 'warning',
      title: 'Délai critique',
      message: `${b.batchNumber || b.batch_number} — livraison avant le ${new Date(b.deadline).toLocaleDateString('fr-FR')}`,
      batchId: b.id,
      timestamp: new Date(),
    }));
}

/** @param {object} batch @param {'start'|'deadline'|'created'|'updated'} field */
export function getBatchDateForFilter(batch, field) {
  switch (field) {
    case 'start':
      return batch.startDate || batch.start_date || null;
    case 'deadline':
      return batch.deadline || null;
    case 'created':
      return batch.createdAt || batch.created_at || null;
    case 'updated':
      return batch.updatedAt || batch.updated_at || null;
    default:
      return null;
  }
}

function startOfDay(isoDateStr) {
  const d = new Date(isoDateStr);
  d.setHours(0, 0, 0, 0);
  return d;
}

function endOfDay(isoDateStr) {
  const d = new Date(isoDateStr);
  d.setHours(23, 59, 59, 999);
  return d;
}

export function batchMatchesDateFilters(batch, filters) {
  const from = filters?.dateFrom?.trim();
  const to = filters?.dateTo?.trim();
  if (!from && !to) return true;

  const field =
    filters?.dateField && filters.dateField !== 'all'
      ? filters.dateField
      : 'deadline';

  const raw = getBatchDateForFilter(batch, field);
  if (!raw) return false;

  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return false;

  if (from && d < startOfDay(from)) return false;
  if (to && d > endOfDay(to)) return false;
  return true;
}

export function batchMatchesSearchQuery(b, rawQuery) {
  const q = String(rawQuery || '').trim().toLowerCase();
  if (!q) return true;
  const haystack = [
    b.batchNumber,
    b.batch_number,
    b.caseNumber,
    b.dentistName,
    b.patientName,
    b.clinicName,
  ]
    .filter((x) => x != null && String(x).trim() !== '')
    .map((x) => String(x).toLowerCase());
  return haystack.some((s) => s.includes(q));
}

export function applyProductionFilters(batches, filters = {}, searchQuery = '') {
  const f = {
    status: 'all',
    priority: 'all',
    machine: 'all',
    deadline: 'all',
    case: 'all',
    dateField: 'deadline',
    dateFrom: '',
    dateTo: '',
    ...filters,
  };

  let filtered = [...(batches || [])];

  if (f.status !== 'all') {
    filtered = filtered.filter((b) => b.status === f.status);
  }
  if (f.priority !== 'all') {
    filtered = filtered.filter((b) => b.priority === f.priority);
  }
  if (f.machine !== 'all') {
    filtered = filtered.filter(
      (b) => b.assignedMachine === f.machine || b.machine?.machine_name === f.machine
    );
  }
  if (f.deadline === 'urgent') {
    filtered = filtered.filter((b) => isCriticalDeadlineBatch(b));
  }
  if (f.case !== 'all') {
    filtered = filtered.filter(
      (b) => b.caseId === f.case || b.case_id === f.case
    );
  }

  filtered = filtered.filter((b) => batchMatchesDateFilters(b, f));
  filtered = filtered.filter((b) => batchMatchesSearchQuery(b, searchQuery));
  return filtered;
}

/**
 * Recherche élargie à tout l'atelier : l'opérateur cherche une commande précise
 * (statut ou texte) plutôt que de consulter la file de son poste.
 */
export function isWorkshopWideSearchActive(filters = {}, searchQuery = '') {
  const status = filters?.status ?? 'all';
  return status !== 'all' || Boolean(String(searchQuery || '').trim());
}

export const DEFAULT_PRODUCTION_FILTERS = {
  status: 'all',
  priority: 'all',
  machine: 'all',
  deadline: 'all',
  case: 'all',
  dateField: 'deadline',
  dateFrom: '',
  dateTo: '',
};
